<template>
  <div class="rentability-header" data-testid="rentability-header">
    <div class="rentability-header__main">
      <div>
        <slot name="filter-options" />
      </div>
      <div
        class="rentability-header__main__filters"
        data-testid="rentability-filters"
      >
        <SpinnerLoader
          v-if="isLoading"
          :size="16"
          :color="NebraskaColors.elementPlaceholder"
        />
        <FilterSelect
          class="rentability-header__main__filter"
          label="Período"
          :value="activePeriodFilter.value"
          :options="periodFilters"
          :default-caption-color="NebraskaColors.themePrimary"
          :disabled="isLoading"
          use-option-label
          render-menu-with-portal
          @change="emitPeriodFilterChange"
        />
        <FilterSelect
          class="rentability-header__main__filter"
          label="Benchmark"
          :value="activeBenchmarkFilter.value"
          :options="benchmarkFilters"
          :default-caption-color="NebraskaColors.elementSecondary"
          :disabled="isLoading"
          use-option-label
          render-menu-with-portal
          @change="emitBenchmarkFilterChange"
        />
      </div>
    </div>
    <div
      v-if="isLoading && !portfolioChartSummary"
      class="rentability-header__summary"
    >
      <SkeletonLoader
        v-for="index in 4"
        :key="index"
        class="rentability-header__summary__item--loader"
      >
        <div class="rentability-header__summary__item__label--loader" />
        <div class="rentability-header__summary__item__value--loader" />
      </SkeletonLoader>
    </div>
    <div
      v-else-if="!hasError"
      data-testid="rentability-summary"
      class="rentability-header__summary"
    >
      <VerticalListDisplay
        class="rentability-header__summary__item"
        :label="profitLabel"
        :value-object="portfolioProfitValue"
        should-hide-value-with-money-toggle
        :is-loading="isLoading"
      />
      <VerticalListDisplay
        class="rentability-header__summary__item"
        :label="returnLabel"
        :value-object="portfolioReturn"
        should-value-be-colored
        :is-loading="isLoading"
      />
      <VerticalListDisplay
        class="rentability-header__summary__item"
        :label="activeBenchmarkFilter.label"
        :value-object="benchmarkReturn"
        :is-loading="isLoading"
      />
      <VerticalListDisplay
        class="rentability-header__summary__item"
        :label="returnComparedToBenchmarkLabel"
        :value-object="portfolioReturnComparedToBenchmark"
        should-value-be-colored
        :is-loading="isLoading"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import {
  FilterSelect,
  SkeletonLoader,
  SpinnerLoader
} from '@warrenbrasil/nebraska-web';
import { VerticalListDisplay } from '@/modules/wealth/components/PerformanceHeader/components/VerticalListDisplay';

import {
  PortfolioChartFilter,
  PortfolioChartSummary
} from '@/modules/wealth/services/portfolio';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';

interface Props {
  isLoading: boolean;
  hasError: boolean;
  portfolioChartSummary: PortfolioChartSummary | null;
  periodFilters: PortfolioChartFilter[];
  benchmarkFilters: PortfolioChartFilter[];
  profitLabel?: string;
  returnLabel?: string;
}
const props = withDefaults(defineProps<Props>(), {
  profitLabel: 'Rendimento',
  returnLabel: 'Rentabilidade'
});

interface Emits {
  (e: 'on-period-filter-change', value: string): void;
  (e: 'on-benchmark-filter-change', value: string): void;
}
const emit = defineEmits<Emits>();

const benchmarkReturn = computed(() => {
  return props.portfolioChartSummary?.benchmarkReturn;
});

const portfolioProfitValue = computed(() => {
  return props.portfolioChartSummary?.profitValue;
});

const portfolioReturn = computed(() => {
  return props.portfolioChartSummary?.return;
});

const portfolioReturnComparedToBenchmark = computed(() => {
  return props.portfolioChartSummary?.returnComparedToBenchmark;
});

const activePeriodFilter = computed(() => {
  const filter = props.periodFilters?.find(filter => filter.active);
  return {
    value: filter?.key || '',
    label: filter?.label || '-'
  };
});

const activeBenchmarkFilter = computed(() => {
  const filter = props.benchmarkFilters?.find(filter => filter.active);
  return {
    value: filter?.key || '',
    label: filter?.summaryLabel || '-',
    comparator: filter?.label || '-'
  };
});

const returnComparedToBenchmarkLabel = computed(() => {
  if (activeBenchmarkFilter.value.comparator.includes('CDI')) {
    return `% ${activeBenchmarkFilter.value.label === 'Meta' ? 'da' : 'do'} ${
      activeBenchmarkFilter.value.label
    }`;
  }

  return `± ${activeBenchmarkFilter.value.label}`;
});

const emitPeriodFilterChange = (value: string) => {
  emit('on-period-filter-change', value);
};

const emitBenchmarkFilterChange = (value: string) => {
  emit('on-benchmark-filter-change', value);
};
</script>

<style lang="less" scoped>
.rentability-header {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  &__main {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: row;

    .to-sm({
      flex-direction: column;
      align-items: flex-start;
    });

    &__filters {
      display: flex;
      align-items: center;

      .to-sm({
        flex-direction: row-reverse;
        margin-top: @size-spacing-x450;
      });
    }

    &__filter,
    &__filter--loader {
      margin-left: @size-spacing-x300;

      .to-sm({
        margin-right: @size-spacing-x300;;
        margin-left: 0;
      });
    }

    &__filter--loader {
      width: 100px;
      height: 32px;
      border-radius: @size-radius-max;
    }
  }

  &__summary {
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;

    &__item {
      min-width: 120px;

      &__label--loader {
        width: 80px;
        height: 24px;
        border-radius: @size-radius-x100;
      }

      &__value--loader {
        width: 60px;
        height: 24px;
        margin-top: @size-spacing-x75;
        border-radius: @size-radius-x100;
      }
    }

    &__item,
    &__item--loader {
      margin-top: @size-spacing-x450;
      margin-right: @size-spacing-x800;
    }
  }
}
</style>
